import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { supabase } from '../../utils/supabase';

export default function ResetPasswordPage() {
    const navigate = useNavigate();

    // State
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [verifying, setVerifying] = useState(true);
    const [error, setError] = useState('');


    useEffect(() => {
        // Check if handling a recovery link by verifying session exists
        const checkSession = async () => {
            try {
                const { data: { session }, error: sessionError } = await supabase.auth.getSession();

                if (sessionError) throw sessionError;

                if (session) {
                    // Session active, no action needed for this state var
                } else {
                    // Listen for the password recovery event
                    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
                        if (event === 'PASSWORD_RECOVERY' || (event === 'SIGNED_IN' && session)) {
                            // User is authenticated
                        }
                    });

                    // If no session after a short delay and no hash in URL, it might be an invalid link or direct access
                    if (!window.location.hash.includes('access_token') && !session) {
                        setError('Invalid or expired reset link. Please request a new one.');
                        // Ensure we clean up any partial state
                        supabase.auth.signOut();
                    }

                    return () => {
                        subscription.unsubscribe();
                    };
                }
            } catch (err: any) {
                console.error('Session check error:', err);
                setError('Failed to verify session. Please try again.');
            } finally {
                setVerifying(false);
            }
        };

        checkSession();
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (password !== confirmPassword) {
            toast.error('Passwords do not match');
            return;
        }

        if (password.length < 8) {
            toast.error('Password must be at least 8 characters long');
            return;
        }

        setLoading(true);
        try {
            const { error } = await supabase.auth.updateUser({ password: password });

            if (error) throw error;

            toast.success('Password updated successfully');

            // Clear history and redirect to dashboard or login
            setTimeout(() => {
                navigate('/login', { replace: true });
            }, 2000);

        } catch (err: any) {
            console.error('Reset password error:', err);
            toast.error(err.message || 'Failed to update password');
        } finally {
            setLoading(false);
        }
    };

    if (verifying) {
        return (
            <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4">
                <div className="bg-white p-10 rounded-[2.5rem] shadow-2xl shadow-zinc-200 w-full max-w-md text-center border border-zinc-100">
                    <div className="w-12 h-12 border-4 border-[#AAD816] border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
                    <p className="text-zinc-500 font-black uppercase tracking-widest text-[10px]">Verifying secure link</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4">
                <div className="bg-white p-10 rounded-[2.5rem] shadow-2xl shadow-zinc-200 w-full max-w-md text-center border border-zinc-100">
                    <div className="w-20 h-20 bg-red-50 rounded-3xl mx-auto mb-6 flex items-center justify-center">
                        <svg className="w-10 h-10 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                    <h2 className="text-2xl font-black text-zinc-950 uppercase tracking-tight mb-2">Link Invalid</h2>
                    <p className="text-zinc-500 font-medium mb-8 leading-relaxed">{error}</p>
                    <Link
                        to="/auth/forgot-password"
                        className="block w-full bg-zinc-950 hover:bg-zinc-900 text-white font-black uppercase tracking-widest py-4 px-4 rounded-2xl transition-all active:scale-[0.98]"
                    >
                        Request New Link
                    </Link>
                    <div className="mt-6">
                        <Link to="/login" className="text-xs font-black uppercase tracking-widest text-zinc-400 hover:text-zinc-950 transition-colors">
                            Back to Sign In
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4 font-sans">
            <div className="bg-white p-10 rounded-[2.5rem] shadow-2xl shadow-zinc-200 w-full max-w-md border border-zinc-100">
                <div className="text-center mb-10">
                    <div className="flex justify-center mb-10">
                        <img src="/gympe.svg" alt="Gympe" className="h-10 w-auto" />
                    </div>
                    <div className="w-16 h-16 bg-zinc-950 rounded-2xl mx-auto mb-6 flex items-center justify-center shadow-xl shadow-zinc-200">
                        <svg className="w-8 h-8 text-[#AAD816]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                    </div>
                    <h2 className="text-3xl font-black text-zinc-950 uppercase tracking-tight">New Password</h2>
                    <p className="text-zinc-500 font-medium mt-3 leading-relaxed">Create a strong password for your account</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="space-y-2">
                        <label htmlFor="password" dangerouslySetInnerHTML={{ __html: 'New Password' }} className="block text-[10px] font-black uppercase tracking-widest text-zinc-400 ml-1" />
                        <input
                            id="password"
                            type="password"
                            required
                            minLength={8}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="w-full px-5 py-4 bg-zinc-50 border border-zinc-200 rounded-2xl focus:ring-2 focus:ring-[#AAD816] transition-all duration-200 font-medium outline-none"
                            placeholder="Min. 8 characters"
                            disabled={loading}
                        />
                    </div>

                    <div className="space-y-2">
                        <label htmlFor="confirmPassword" dangerouslySetInnerHTML={{ __html: 'Confirm Password' }} className="block text-[10px] font-black uppercase tracking-widest text-zinc-400 ml-1" />
                        <input
                            id="confirmPassword"
                            type="password"
                            required
                            minLength={8}
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            className="w-full px-5 py-4 bg-zinc-50 border border-zinc-200 rounded-2xl focus:ring-2 focus:ring-[#AAD816] transition-all duration-200 font-medium outline-none"
                            placeholder="Re-enter password"
                            disabled={loading}
                        />
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-zinc-950 hover:bg-zinc-900 text-white font-black uppercase tracking-widest py-4 px-4 rounded-2xl transition-all active:scale-[0.98] shadow-lg shadow-zinc-200 disabled:opacity-50 disabled:cursor-not-allowed mt-4"
                    >
                        {loading ? (
                            <div className="flex items-center justify-center">
                                <div className="w-4 h-4 border-2 border-[#AAD816] border-t-transparent rounded-full animate-spin mr-3"></div>
                                Updating...
                            </div>
                        ) : (
                            'Reset Password'
                        )}
                    </button>
                </form>
            </div>
        </div>
    );
}
